/**
 * OTP throttling, read from Auth's own contract (ZOY-72).
 *
 * Auth distinguishes three 429 causes and supplies the real remaining wait; a
 * client that collapses them tells the user the wrong thing — "too many
 * attempts" for what was actually a 60-second resend gap. Auth's ack also
 * carries `resendCooldownSeconds` with the explicit instruction to drive the
 * resend countdown from it rather than a hardcoded constant.
 *
 * Every number here therefore originates on the server. Nothing is invented:
 * we never guess a wait, only count down one Auth stated.
 */

import type { IntlShape } from 'react-intl';
import { ApiError } from '@/core/api/errors';

export const OTP_THROTTLE_CODES = [
  'OTP_RESEND_COOLDOWN',
  'OTP_SEND_WINDOW_EXCEEDED',
  'OTP_LOCKED',
] as const;

export type OtpThrottleCode = (typeof OTP_THROTTLE_CODES)[number];

export interface OtpThrottle {
  /** Null when Auth sent a 429 whose code we do not recognise. */
  code: OtpThrottleCode | null;
  /** Auth's real remaining wait; undefined if it sent none. */
  retryAfterSeconds: number | undefined;
}

/** A 429 from any OTP surface, or null if this error is something else. */
export function otpThrottleFrom(err: unknown): OtpThrottle | null {
  if (!(err instanceof ApiError) || err.status !== 429) return null;
  const raw = err.authCode;
  const code = OTP_THROTTLE_CODES.find((c) => c === raw) ?? null;
  return { code, retryAfterSeconds: err.retryAfterSeconds };
}

/**
 * ZOY-729/768: Auth could not hand the code over for delivery (SMS vendor
 * refused, delivery service down, limit reached — one code for every cause,
 * account-neutral). HTTP 503, `retryAfterSeconds` = the resend cooldown: the
 * attempt counted as a send, so asking sooner would answer a 429. No code is
 * on its way, so a client must never open the code step on it.
 */
export const OTP_DELIVERY_FAILED = 'OTP_DELIVERY_FAILED';

export function otpDeliveryFailedFrom(
  err: unknown,
): { retryAfterSeconds: number | undefined } | null {
  if (!(err instanceof ApiError) || err.status !== 503) return null;
  if (err.authCode !== OTP_DELIVERY_FAILED) return null;
  return { retryAfterSeconds: err.retryAfterSeconds };
}

/** Why the resend control is unavailable: a throttle, or the last code not having been sent. */
export type ResendBlockReason = OtpThrottleCode | typeof OTP_DELIVERY_FAILED;

/**
 * Catalog keys per cause. OTP_LOCKED is about WRONG CODES, not about asking
 * for a new one, so it must never read as a resend problem.
 */
export function otpThrottleCopy(code: ResendBlockReason | null): {
  titleKey: string;
  bodyKey: string;
} {
  switch (code) {
    case OTP_DELIVERY_FAILED:
      return { titleKey: 'otp.notSentTitle', bodyKey: 'otp.notSentNote' };
    case 'OTP_RESEND_COOLDOWN':
      return { titleKey: 'otp.cooldownTitle', bodyKey: 'otp.cooldownBody' };
    case 'OTP_SEND_WINDOW_EXCEEDED':
      return { titleKey: 'otp.windowTitle', bodyKey: 'otp.windowBody' };
    case 'OTP_LOCKED':
      return { titleKey: 'otp.lockedTitle', bodyKey: 'otp.lockedBody' };
    default:
      return { titleKey: 'otp.throttledTitle', bodyKey: 'otp.throttledGeneric' };
  }
}

/** When a wait that starts now ends. `seconds` always comes from Auth. */
export function cooldownEndsAt(seconds: number | undefined, now: number): number | null {
  if (seconds === undefined || !Number.isFinite(seconds) || seconds <= 0) return null;
  return now + seconds * 1000;
}

/** Whole seconds left, 0 once elapsed. */
export function remainingSeconds(endsAt: number | null, now: number): number {
  if (endsAt === null) return 0;
  return Math.max(0, Math.ceil((endsAt - now) / 1000));
}

/**
 * OTP_LOCKED means the CHALLENGE is locked — asking for another code is not
 * the way out, so the resend control stays unavailable for that wait too.
 */
export function blocksResend(code: ResendBlockReason | null): boolean {
  return code !== null;
}

/**
 * A server wait split for people: seconds under a minute, otherwise whole
 * minutes (and hours past 60). Minutes round UP — a rounded-down wait would
 * send the user back before Auth is ready and earn them another refusal.
 */
export function waitParts(total: number): { hours: number; minutes: number; seconds: number } {
  const secs = Math.max(0, Math.ceil(total));
  if (secs < 60) return { hours: 0, minutes: 0, seconds: secs };
  const mins = Math.ceil(secs / 60);
  return { hours: Math.floor(mins / 60), minutes: mins % 60, seconds: 0 };
}

/** "38 minutes", "1 hour and 5 minutes", "۳۸ دقیقه" — in the active locale. */
export function formatWait(intl: IntlShape, total: number): string {
  const p = waitParts(total);
  const unit = (value: number, u: 'hour' | 'minute' | 'second') =>
    intl.formatNumber(value, { style: 'unit', unit: u, unitDisplay: 'long' });
  if (p.hours === 0 && p.minutes === 0) return unit(p.seconds, 'second');
  const parts: string[] = [];
  if (p.hours > 0) parts.push(unit(p.hours, 'hour'));
  if (p.minutes > 0) parts.push(unit(p.minutes, 'minute'));
  return intl.formatList(parts, { type: 'conjunction' });
}
